import { useEffect, useState } from "react";
import api, { API } from "../lib/apiClient";
import { resolveImageUrl } from "../lib/images";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, LogOut, Pencil, Trash2, ArrowLeft, Loader2, Users as UsersIcon, Upload, X, GripVertical, FileDown, Camera } from "lucide-react";
import { toast, Toaster } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMPTY = { name: "", category: "", description: "", specs: "", price_info: "", show_price: true, image_url: "", sort_order: 0, is_active: true };

function SortableRow({ it, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    background: isDragging ? "#F8FAFC" : undefined,
  };
  return (
    <TableRow ref={setNodeRef} style={style} data-testid={`admin-row-${it.id}`}>
      <TableCell className="w-8 cursor-grab active:cursor-grabbing text-slate-400 hover:text-[#1B3A6B]" {...attributes} {...listeners} data-testid={`admin-handle-${it.id}`}>
        <GripVertical className="w-5 h-5" />
      </TableCell>
      <TableCell>
        {it.image_url ? <img src={resolveImageUrl(it.image_url)} alt="" className="w-14 h-14 rounded object-cover" /> : <div className="w-14 h-14 rounded bg-slate-100" />}
      </TableCell>
      <TableCell className="font-medium text-slate-900">{it.name}</TableCell>
      <TableCell><span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{it.category}</span></TableCell>
      <TableCell>
        <span className={`inline-flex w-2 h-2 rounded-full ${it.is_active ? "bg-emerald-500" : "bg-slate-300"}`} /> {it.is_active ? "Oui" : "Non"}
      </TableCell>
      <TableCell className="text-right space-x-1">
        <Button variant="ghost" size="icon" onClick={() => onEdit(it)} data-testid={`admin-edit-${it.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button variant="ghost" size="icon" onClick={() => onDelete(it)} data-testid={`admin-delete-${it.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
      </TableCell>
    </TableRow>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [openDelete, setOpenDelete] = useState(null);
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    // Reassign sort_order in steps of 10 so future insertions are easy
    const withOrder = reordered.map((it, idx) => ({ ...it, sort_order: (idx + 1) * 10 }));
    setItems(withOrder);
    try {
      await api.patch("/admin/equipment/reorder", withOrder.map((it) => ({ id: it.id, sort_order: it.sort_order })));
      toast.success("Ordre enregistré");
    } catch {
      toast.error("Erreur d'enregistrement, rechargement…");
      load();
    }
  };

  const resolveImg = resolveImageUrl;

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 5 Mo)"); return; }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image téléversée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'upload");
    } finally { setUploading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/equipment", { params: { active_only: false } });
      setItems(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("ingelec_admin_token");
        nav("/admin/login");
      } else toast.error("Erreur de chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const logout = () => {
    localStorage.removeItem("ingelec_admin_token");
    nav("/admin/login");
  };

  const openCreate = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({ ...it, specs: (it.specs || []).join("\n") });
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      specs: form.specs.split("\n").map((s) => s.trim()).filter(Boolean),
      sort_order: Number(form.sort_order) || 0,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/equipment", payload);
        toast.success("Matériel ajouté");
      } else {
        await api.put(`/admin/equipment/${editing}`, payload);
        toast.success("Matériel mis à jour");
      }
      close();
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!openDelete) return;
    try {
      await api.delete(`/admin/equipment/${openDelete.id}`);
      toast.success("Matériel supprimé");
      setOpenDelete(null);
      load();
    } catch { toast.error("Erreur de suppression"); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="page-admin">
      <Toaster position="top-right" />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-slate-500 hover:text-[#1B3A6B] flex items-center gap-1.5 text-sm" data-testid="admin-back-site">
              <ArrowLeft className="w-4 h-4" /> Site
            </Link>
            <div className="h-5 w-px bg-slate-200" />
            <span className="font-display font-bold text-[#1B3A6B]">Admin · Catalogue</span>
          </div>
          <div className="flex items-center gap-3">
            <a href={`${API}/equipment/export.pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="admin-pdf-export">
                <FileDown className="w-4 h-4 mr-1.5" /> PDF
              </Button>
            </a>
            <Link to="/admin/realisations">
              <Button variant="outline" data-testid="admin-realisations-link">
                <Camera className="w-4 h-4 mr-1.5" /> Chantiers
              </Button>
            </Link>
            <Link to="/admin/users">
              <Button variant="outline" data-testid="admin-users-link">
                <UsersIcon className="w-4 h-4 mr-1.5" /> Utilisateurs
              </Button>
            </Link>
            <Button onClick={openCreate} className="bg-[#FBBF24] hover:bg-[#F59E0B] text-[#0F172A] font-semibold rounded-full" data-testid="admin-add-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Ajouter
            </Button>
            <Button variant="ghost" onClick={logout} data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-1.5" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…</div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="w-20">Image</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Actif</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
                    {items.map((it) => (
                      <SortableRow key={it.id} it={it} onEdit={openEdit} onDelete={setOpenDelete} />
                    ))}
                  </SortableContext>
                  {items.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-10">Aucun matériel.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </DndContext>
          )}
        </div>
        <p className="mt-3 text-xs text-slate-500">Glissez les lignes par la poignée à gauche pour réorganiser l'ordre d'affichage sur le site.</p>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Ajouter un matériel" : "Modifier le matériel"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Nom *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="admin-form-name" />
              </div>
              <div>
                <Label>Catégorie *</Label>
                <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5" data-testid="admin-form-category" />
              </div>
              <div>
                <Label>Ordre d'affichage</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1.5" data-testid="admin-form-sort" />
              </div>
              <div className="md:col-span-2">
                <Label>Image du matériel</Label>
                <div className="mt-1.5 flex flex-col sm:flex-row gap-3">
                  <div className="shrink-0">
                    {form.image_url ? (
                      <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                        <img src={resolveImg(form.image_url)} alt="aperçu" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, image_url: "" })}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 backdrop-blur flex items-center justify-center shadow hover:bg-white"
                          data-testid="admin-form-image-clear"
                          aria-label="Retirer l'image"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-32 h-32 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center text-slate-400 text-xs text-center px-2">
                        Aucune image
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <label className={`flex items-center justify-center gap-2 px-4 h-11 rounded-md border-2 border-dashed cursor-pointer transition-colors ${uploading ? "border-slate-200 bg-slate-50 text-slate-400" : "border-[#1B3A6B]/40 bg-[#1B3A6B]/5 text-[#1B3A6B] hover:bg-[#1B3A6B]/10"}`}>
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      <span className="text-sm font-medium">{uploading ? "Téléversement…" : "Téléverser une photo"}</span>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
                        data-testid="admin-form-image-upload"
                      />
                    </label>
                    <p className="text-xs text-slate-500">JPG, PNG, WebP ou GIF · max 5 Mo</p>
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                      <div className="relative flex justify-center"><span className="px-2 text-[10px] uppercase tracking-wider bg-white text-slate-400">ou coller une URL externe</span></div>
                    </div>
                    <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…" data-testid="admin-form-image" />
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <Label>Description *</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" data-testid="admin-form-description" />
              </div>
              <div className="md:col-span-2">
                <Label>Caractéristiques (une par ligne)</Label>
                <Textarea rows={6} value={form.specs} onChange={(e) => setForm({ ...form, specs: e.target.value })} className="mt-1.5 font-mono text-sm" data-testid="admin-form-specs" />
              </div>
              <div className="md:col-span-2">
                <Label>Tarification</Label>
                <Textarea rows={2} value={form.price_info || ""} onChange={(e) => setForm({ ...form, price_info: e.target.value })} className="mt-1.5" data-testid="admin-form-price" />
                <div className="mt-2 flex items-center gap-3">
                  <Switch checked={form.show_price !== false} onCheckedChange={(v) => setForm({ ...form, show_price: v })} data-testid="admin-form-show-price" />
                  <Label className="cursor-pointer" onClick={() => setForm({ ...form, show_price: form.show_price === false })}>
                    Afficher le tarif sur le site public
                  </Label>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="admin-form-active" />
                <Label>Visible sur le site</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close} data-testid="admin-form-cancel">Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1B3A6B] hover:bg-[#142c54]" data-testid="admin-form-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing === "new" ? "Ajouter" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!openDelete} onOpenChange={(o) => !o && setOpenDelete(null)}>
        <DialogContent data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer ce matériel ?</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">"{openDelete?.name}" sera définitivement supprimé du catalogue.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDelete(null)} data-testid="admin-delete-cancel">Annuler</Button>
            <Button onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="admin-delete-confirm">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
